import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import './InvestmentPortfolio.scss';

interface Investment {
    symbol: string;
    allocationPercent: number;
    preserved: boolean;
    color?: string;
}

interface InvestmentPortfolioProps {
    investments: Investment[];
    fromUpdate: boolean;
}

const baseColors = ['#0066A6', '#7CB6E3', '#A8B8C4', '#ABE2FB'];

const generateSoftColorPalette = (count: number) => {
    const colors: string[] = [...baseColors];

    if (count <= baseColors.length) {
        return colors.slice(0, count);
    }

    let variationIndex = 0;
    while (colors.length < count) {
        const base = baseColors[variationIndex % baseColors.length];
        const hsl = hexToHsl(base);

        const hueShift = ((variationIndex + 1) * 25) % 360;
        const hue = (hsl.h + hueShift) % 360;

        const saturation = Math.max(20, hsl.s - 30 + (variationIndex % 3) * 5);

        const lightness = Math.min(80, Math.max(35, hsl.l + (variationIndex % 5) * 5));

        colors.push(hslToHex(hue, saturation, lightness));

        variationIndex++;
    }

    return colors;
};

function hexToHsl(hex: string) {
    hex = hex.replace(/^#/, '');
    let r = parseInt(hex.substring(0, 2), 16) / 255;
    let g = parseInt(hex.substring(2, 4), 16) / 255;
    let b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    let h = 0,
        s = 0,
        l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h *= 60;
    }
    return { h, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number) {
    s /= 100;
    l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
        const color = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
        return Math.round(255 * color)
            .toString(16)
            .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

const DEFAULT_COLORS = generateSoftColorPalette(20);

const InvestmentPortfolio: React.FC<InvestmentPortfolioProps> = ({ investments, fromUpdate }) => {
    const data = useMemo(() => {
        return investments.map((inv, index) => ({
            name: inv.symbol,
            value: inv.allocationPercent,
            color: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
        }));
    }, [investments]);

    const renderCustomLabel = (props: any) => {
        const { cx, cy, midAngle, innerRadius, outerRadius, value } = props;
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                className='investment-portfolio__pie-label'
            >
                {`${value}%`}
            </text>
        );
    };

    return (
        <div className={`investment-portfolio ${!fromUpdate ? 'investment-portfolio--flex' : ''}`}>
            <div className='investment-portfolio__chart-container'>
                <ResponsiveContainer
                    width='100%'
                    height='100%'
                >
                    <PieChart>
                        <Pie
                            data={data}
                            cx='50%'
                            cy='50%'
                            innerRadius={80}
                            outerRadius={130}
                            paddingAngle={2}
                            dataKey='value'
                            labelLine={false}
                            label={renderCustomLabel}
                            animationDuration={0}
                        >
                            {data.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                />
                            ))}
                        </Pie>
                        <text
                            x='50%'
                            y='50%'
                            textAnchor='middle'
                            dominantBaseline='middle'
                            className='investment-portfolio__center-text'
                        >
                            <tspan
                                x='50%'
                                dy='-10'
                            >
                                Investment
                            </tspan>
                            <tspan
                                x='50%'
                                dy='25'
                            >
                                Model
                            </tspan>
                        </text>
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div className={`investment-portfolio__legend ${fromUpdate ? 'investment-portfolio__legend--row' : ''}`}>
                {data.map((entry, index) => (
                    <div
                        key={`legend-${index}`}
                        className='investment-portfolio__legend-item'
                    >
                        <span
                            className='investment-portfolio__legend-color'
                            style={{ backgroundColor: entry.color }}
                        />
                        <span className='investment-portfolio__legend-name'>{entry.name}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default InvestmentPortfolio;
